import React from 'react';
import { useParams } from 'react-router-dom';
import ProfilePage from './ProfilePage';
import { Typography, Box, Paper, Link } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from '../constants/site';
import { useUser } from '../contexts/UserContext';
import { useProfileQuery } from '../hooks/useProfileQuery';

/**
 * 他人（または自分）の公開プロフィール。
 *
 * **データは持たない。** 以前はここで fetch して state に持ち、それを
 * ProfilePage へ prop で渡していたが、ユーザーを切り替えたとき
 * 「前の人が次の人のページとして表示され続ける」原因になっていた
 * (詳細は useProfileQuery のコメント)。
 *
 * 今は ProfilePage が自分で useProfileQuery を呼ぶ。ここで同じフックを呼んでも
 * **キャッシュキーが同じなので通信は1回**。所有者判定に user_id が要るだけ。
 */
const PublicProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { data: profile, isError } = useProfileQuery(username, !!username);

  // 自分の公開ページを見ているか。
  // **UserContext が既に持っている user_id を使うこと。**
  // 以前は表示中のプロフィールが変わるたびに /user/id を叩き直していたが、
  // 閲覧者の ID はアプリ起動時から分かっていて、この判定のために通信は要らない。
  const { user } = useUser();
  const isOwnProfile = !!profile && !!user && user.user_id === profile.user_id;

  if (isError) {
    return (
      <>
        <Helmet>
          <title>プロフィールが見つかりません | {SITE_NAME}</title>
        </Helmet>
        {/* レイアウトの中で完結させる。以前は生の Typography だけで、
            ヘッダーの下に一行だけ浮く状態だった */}
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            プロフィールが見つかりません
          </Typography>
          <Typography variant="body2" color="text.secondary">
            @{username} は存在しないか、非公開に設定されています。
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* key を付けないこと。付けるとユーザー間の遷移で毎回リマウントになり、
          キャッシュが効いていても子タブの状態ごと作り直される。
          タブとスクロールのリセットは ProfilePage 側が username を見て行う。 */}
      <ProfilePage isPublic isOwnProfile={isOwnProfile} />

      {isOwnProfile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            textAlign: 'center',
            bgcolor: 'background.paper',
            borderTop: 2,
            borderColor: 'primary.main',
            zIndex: 1001
          }}
        >
          <Typography variant="body2">
            現在パブリックページを表示しています。個人ページに戻る場合は
            <Link href="/profile" >
            こちら
            </Link>
          </Typography>
        </Paper>
      )}
    </>
  );
};

export default PublicProfilePage;
